<template>
  <div style="margin-top: 60px">
    <div
      style="display: flex; justify-content: space-between; align-items: center"
    >
      <div>
        <h1 style="font-size: large; font-weight: 700">Wallet</h1>
        <p style="color: #6b7280; font-size: 14px">
          View and manage seller's Wallet
        </p>
      </div>

      <div style="display: flex; align-items: center; gap: 16px">
        <a-button
          :loading="exportLoading"
          @click="exportExcel"
          style="border-radius: 6px; background: #f9fafb; color: #374151"
          icon="download"
        >
          Export
        </a-button>
      </div>
    </div>

    <div>
      <a-row
        type="flex"
        align="top"
        :gutter="[16, 16]"
        style="margin-top: 30px"
      >
        <a-col :span="8">
          <div
            style="
              background: #fff;
              border-radius: 6px;
              padding: 20px;
              border: 1px solid #e6e6e6;
            "
          >
            <a-statistic
              :value-style="{
                color: '#05004E',
                fontSize: '16px',
                fontWeight: 'bold',
              }"
              :title-style="{ fontSize: '16px' }"
              :value="walletStats.availableForWithdrawal || 0"

            >
              <template #title>
                <span>Available Balance</span>
              </template>
            </a-statistic>
          </div>
        </a-col>

        <a-col :span="8">
          <div
            style="
              background: #fff;
              border-radius: 6px;
              padding: 20px;
              border: 1px solid #e6e6e6;
            "
          >
            <a-statistic
              :value-style="{
                color: '#05004E',
                fontSize: '16px',
                fontWeight: 'bold',
              }"
              :title-style="{ fontSize: '16px' }"
              :value="walletStats.pendingSettlement || 0"

            >
              <template #title>
                <span>Pending Settlement</span>
              </template>
            </a-statistic>
          </div>
        </a-col>

        <a-col :span="8">
          <div
            style="
              background: #fff;
              border-radius: 6px;
              padding: 20px;
              border: 1px solid #e6e6e6;
            "
          >
            <a-statistic
              :value-style="{
                color: '#05004E',
                fontSize: '16px',
                fontWeight: 'bold',
              }"
              :title-style="{ fontSize: '16px' }"
              :value="walletStats.totalSettled"
            >
              <template #title>
                <span>Total Settled</span>
              </template>
            </a-statistic>
          </div>
        </a-col>
      </a-row>
    </div>

    <!-- Tabs for Transactions and Disbursements -->
    <a-tabs
      v-model="activeWalletTab"
      style="margin-top: 32px"
      @change="onWalletTabChange"
    >
      <a-tab-pane key="1" tab="Transactions">
        <Transactions ref="transactionsComponent" :sellerId="sellerId" />
      </a-tab-pane>
      
      <a-tab-pane key="2" tab="Disbursements">
        <Disbursements ref="disbursementsComponent" :sellerId="sellerId" />
      </a-tab-pane>
    </a-tabs>
  </div>
</template>
<script>
import Transactions from "./Transactions.vue";
import Disbursements from "./Disbursements.vue";
import * as XLSX from "xlsx";
import moment from "moment";
import FinancingAndPaymentsService from "@/api/financing-and-payments.service.js";
import { formatAmount } from "@/utils/index.js";

export default {
  name: "Wallet",
  components: {
    Transactions,
    Disbursements,
  },
  props: {
    sellerId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activeWalletTab: this.$route.query.walletTab || "1",
      exportLoading: false,
      walletStats: {
        availableForWithdrawal: 0,
        pendingSettlement: 0,
        totalSettled: 0,
      },
      statsLoading: false,
    };
  },
  mounted() {
    this.fetchWalletStats();
  },
  watch: {
    // Watch for route changes to sync tab
    '$route.query.walletTab'(newVal) {
      if (newVal && newVal !== this.activeWalletTab) {
        this.activeWalletTab = newVal;
      }
    },
    sellerId() {
      this.fetchWalletStats();
    },
  },
  methods: {
    async fetchWalletStats() {
      if (!this.sellerId) return;
      
      this.statsLoading = true;
      try {
        const response = await FinancingAndPaymentsService.getSettlementStats(this.sellerId);
        
        if (response.data?.code === 200) {
          const data = response.data.data;
          this.walletStats = {
            availableForWithdrawal: formatAmount(data.availableForWithdrawal || 0),
            pendingSettlement: formatAmount(data.pendingSettlement || 0),
            totalSettled: formatAmount(data.totalSettled || 0),
          };
        } else {
          console.error("Failed to fetch wallet stats:", response.data?.message);
          this.$message.warning("Failed to load wallet statistics");
        }
      } catch (error) {
        console.error("Error fetching wallet stats:", error);
        this.$message.error("Failed to load wallet statistics");
      } finally {
        this.statsLoading = false;
      }
    },
    onWalletTabChange(newKey) {
      // Update the route query with the new tab
      this.$router.replace({
        query: { ...this.$route.query, walletTab: newKey },
      }).catch(() => {});
      this.activeWalletTab = newKey;
    },
    async exportExcel() {
      try {
        this.exportLoading = true;
        
        // Create a new workbook
        const wb = XLSX.utils.book_new();
        
        // Get data from both components
        const transactionsData = this.$refs.transactionsComponent?.getExportData() || [];
        const disbursementsData = this.$refs.disbursementsComponent?.getExportData() || [];
        
        // Add Transactions sheet
        if (transactionsData.length > 0) {
          const transactionsWs = XLSX.utils.json_to_sheet(transactionsData);
          XLSX.utils.book_append_sheet(wb, transactionsWs, "Transactions");
        }
        
        // Add Disbursements sheet
        if (disbursementsData.length > 0) {
          const disbursementsWs = XLSX.utils.json_to_sheet(disbursementsData);
          XLSX.utils.book_append_sheet(wb, disbursementsWs, "Disbursements");
        }
        
        // Add Wallet Summary sheet
        const summaryData = [
          { Metric: "Available Balance", Value: formatAmount(this.walletStats.availableForWithdrawal || 0) },
          { Metric: "Pending Settlement", Value: formatAmount(this.walletStats.pendingSettlement || 0) },
          { Metric: "Total Settled", Value: formatAmount(this.walletStats.totalSettled || 0) },
          { Metric: "Export Date", Value: moment().format("YYYY-MM-DD HH:mm:ss") },
          { Metric: "Seller ID", Value: this.sellerId }
        ];
        const summaryWs = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, summaryWs, "Wallet Summary");
        
        // Generate filename
        const fileName = `Wallet_Export_Seller_${this.sellerId}_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`;
        
        // Save the file
        XLSX.writeFile(wb, fileName);
        
        this.$message.success("Wallet data exported successfully!");
      } catch (error) {
        console.error("Export error:", error);
        this.$message.error("Failed to export wallet data. Please try again.");
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>
<style scoped></style>
